import { CheckCircle2, XCircle, Loader2, ExternalLink } from "lucide-react";

interface DeliveryRecord {
  id: string;
  connection_label: string;
  connection_type: string;
  event_class: string;
  status: "delivered" | "failed" | "retrying";
  response_code: number;
  duration_ms: number;
  external_reference?: string;
  error?: string;
  created_at: string;
}

interface DeliveryHistoryProps {
  records: DeliveryRecord[];
}

const STATUS_CONFIG = {
  delivered: { label: "Delivered",  color: "text-status-up",   bg: "bg-status-up/10",   border: "border-status-up/30",   icon: <CheckCircle2 className="h-3 w-3" /> },
  failed:    { label: "Failed",     color: "text-status-down", bg: "bg-status-down/10", border: "border-status-down/30", icon: <XCircle className="h-3 w-3" /> },
  retrying:  { label: "Retrying",   color: "text-yellow-500",  bg: "bg-yellow-500/10",  border: "border-yellow-500/30",  icon: <Loader2 className="h-3 w-3 animate-spin" /> },
};

export function DeliveryHistory({ records }: DeliveryHistoryProps) {
  if (records.length === 0) {
    return (
      <div className="border-2 border-dashed border-foreground/20 p-6 text-center">
        <p className="text-[10px] font-black uppercase text-muted-foreground">No deliveries yet.</p>
        <p className="text-[9px] text-muted-foreground uppercase mt-1">Deliveries will appear here after the first incident alert.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {records.map(r => {
        const s = STATUS_CONFIG[r.status];
        return (
          <div key={r.id} className="flex items-center gap-4 border border-foreground/10 px-4 py-2.5 text-[9px] font-bold uppercase">
            {/* Timestamp */}
            <span className="text-muted-foreground w-28 shrink-0">
              {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>

            {/* Connection */}
            <span className="font-black w-24 shrink-0 truncate">{r.connection_label}</span>

            {/* Event class */}
            <span className="text-muted-foreground w-36 shrink-0 truncate">{r.event_class}</span>

            {/* Status badge */}
            <div className={`flex items-center gap-1 px-2 py-0.5 border ${s.bg} ${s.border} ${s.color} shrink-0`}>
              {s.icon}
              <span>{s.label}</span>
            </div>

            {/* Response code */}
            <span className="text-muted-foreground w-12 shrink-0">{r.response_code || "—"}</span>

            {/* Duration */}
            <span className="text-muted-foreground w-16 shrink-0">{r.duration_ms}ms</span>

            {/* External reference */}
            {r.external_reference ? (
              <span className="font-mono text-primary truncate max-w-[120px]">
                {r.external_reference}
              </span>
            ) : (
              <span className="text-muted-foreground/40">—</span>
            )}

            {/* Error */}
            {r.error && (
              <span className="text-status-down truncate max-w-[160px]">{r.error}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
